import React from 'react';
import { differenceInCalendarDays, isPast, parseISO } from 'date-fns';
import { Aggregator, AggregatorStatus, Status } from '../../../domain/types';
import { Property } from '../../../providers/api/dtos';
import { MarketingOpportunityTrigger } from '../../types';
import { useAgentByPropertyId } from '../../../hooks/use-agent/useAgent';
import { isPropertyPublishedOnDoveIt } from '../../../property/utils';
import useUserPermissions from '../../../hooks/use-user-permissions/useUserPermissions';

export interface UsePropertyMarketingOpportunitiesResult {
  triggers: MarketingOpportunityTrigger[];
  getTriggersByTypes: (
    types: MarketingOpportunityTrigger['type'] | MarketingOpportunityTrigger['type'][],
  ) => MarketingOpportunityTrigger[];
}

const RECENTLY_PUBLISHED_DAYS = 15;

function isLiveOnDoveIt(property: Property): { isLive: false } | { isLive: true, liveSinceInDays: number } {
  if (property.status !== Status.LIVE) {
    return { isLive: false };
  }

  const doveItPublication = property.publishedOn?.find(
    (publication) => publication.aggregator === Aggregator.DOVE_IT
      && publication.aggregatorStatus === AggregatorStatus.SUCCESS
      && !!publication.publishedAt,
  );

  if (!doveItPublication?.publishedAt) {
    return { isLive: false };
  }

  const publishedAt = parseISO(doveItPublication.publishedAt);

  const daysSincePublication = differenceInCalendarDays(
    new Date(),
    publishedAt,
  );

  if (!isPast(publishedAt)) {
    return { isLive: false };
  }

  return {
    isLive: true,
    liveSinceInDays: daysSincePublication,
  };
}

export default function usePropertyMarketingOpportunities(
  property?: Property,
): UsePropertyMarketingOpportunitiesResult {
  const userPermissions = useUserPermissions(['marketing']);

  const { data: agent } = useAgentByPropertyId(property?.id);

  const triggers = React.useMemo<MarketingOpportunityTrigger[]>(() => {
    if (!property?.id || !userPermissions.marketing.canSeeSuggestions || !isPropertyPublishedOnDoveIt(property)) {
      return [];
    }

    if (property.status === Status.VENDUTO) {
      return [{
        type: 'property-sold',
        propertyId: property.id,
        agentId: agent?.id,
      }];
    }

    const isLive = isLiveOnDoveIt(property);
    if (isLive.isLive) {
      if (isLive.liveSinceInDays <= RECENTLY_PUBLISHED_DAYS) {
        return [{
          type: 'property-recently-published',
          propertyId: property.id,
          agentId: agent?.id,
        }];
      }

      return [{
        type: 'property-live',
        propertyId: property.id,
        agentId: agent?.id,
      }];
    }

    return [];
  }, [agent?.id, property, userPermissions.marketing.canSeeSuggestions]);

  const getTriggersByTypes = React.useCallback(
    (types: MarketingOpportunityTrigger['type'] | MarketingOpportunityTrigger['type'][]) => {
      const typeArray = Array.isArray(types) ? types : [types];

      return triggers.filter((trigger) => typeArray.includes(trigger.type));
    },
    [triggers],
  );

  return {
    triggers,
    getTriggersByTypes,
  };
}
